import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import * as blogApi from '../services/medusa/blog';

// Estado global del blog. Antes vivía en localStorage (cada navegador veía
// datos distintos); ahora todo se lee/escribe contra el backend Medusa:
//   - Lectura pública  → /store/blog/*   (posts publicados, categorías, ajustes)
//   - Escritura admin  → /admin/blog/*   (token de admin en localStorage)
// La interfaz expuesta se mantiene igual que antes para no tocar los
// componentes que la consumen; los métodos de escritura ahora son async.

const BlogContext = createContext();

const hasAdminToken = () => !!(localStorage.getItem('admin_token') || '').trim();

export function BlogProvider({ children }) {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [media, setMedia] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  // Carga los posts: si hay sesión de admin trae TODOS (borradores incluidos),
  // si no, solo los publicados (para la web pública).
  const refreshPosts = useCallback(async () => {
    try {
      const list = hasAdminToken()
        ? await blogApi.fetchAllPosts()
        : await blogApi.fetchPublishedPosts();
      setPosts(list);
    } catch (e) {
      console.warn('BlogContext: no se pudieron cargar los posts', e);
    }
  }, []);

  const refreshCategories = useCallback(async () => {
    try {
      setCategories(await blogApi.fetchPublicCategories());
    } catch (e) {
      console.warn('BlogContext: no se pudieron cargar las categorías', e);
    }
  }, []);

  const refreshMedia = useCallback(async () => {
    if (!hasAdminToken()) return;
    try {
      setMedia(await blogApi.fetchMedia());
    } catch (e) {
      console.warn('BlogContext: no se pudo cargar la biblioteca de medios', e);
    }
  }, []);

  const refreshSettings = useCallback(async () => {
    try {
      setSettings(await blogApi.fetchSettings());
    } catch (e) {
      console.warn('BlogContext: no se pudieron cargar los ajustes', e);
    }
  }, []);

  // Carga inicial
  useEffect(() => {
    (async () => {
      await Promise.all([refreshPosts(), refreshCategories(), refreshMedia(), refreshSettings()]);
      setLoading(false);
    })();
  }, [refreshPosts, refreshCategories, refreshMedia, refreshSettings]);

  // --- Posts CRUD ---
  const getPost = useCallback((id) => posts.find(p => p.id === id), [posts]);
  const getPostBySlug = useCallback((slug) => posts.find(p => p.slug === slug), [posts]);

  const createPost = useCallback(async (postData) => {
    const newPost = await blogApi.createPost(postData);
    setPosts(prev => [newPost, ...prev]);
    return newPost;
  }, []);

  const updatePost = useCallback(async (id, data) => {
    const updated = await blogApi.updatePost(id, data);
    setPosts(prev => prev.map(p => (p.id === id ? updated : p)));
    return updated;
  }, []);

  const deletePost = useCallback(async (id) => {
    await blogApi.deletePost(id);
    setPosts(prev => prev.filter(p => p.id !== id));
  }, []);

  // --- Categories CRUD ---
  const addCategory = useCallback(async (cat) => {
    const newCat = await blogApi.createCategory(cat);
    setCategories(prev => [...prev, newCat]);
    return newCat;
  }, []);

  const updateCategory = useCallback(async (id, data) => {
    const updated = await blogApi.updateCategory(id, data);
    setCategories(prev => prev.map(c => (c.id === id ? updated : c)));
    return updated;
  }, []);

  const deleteCategory = useCallback(async (id) => {
    await blogApi.deleteCategory(id);
    setCategories(prev => prev.filter(c => c.id !== id));
  }, []);

  // --- Media ---
  const addMedia = useCallback(async (mediaItem) => {
    const item = await blogApi.createMedia(mediaItem);
    setMedia(prev => [item, ...prev]);
    return item;
  }, []);

  const deleteMedia = useCallback(async (id) => {
    await blogApi.deleteMedia(id);
    setMedia(prev => prev.filter(m => m.id !== id));
  }, []);

  // Sube un archivo (imagen o video) a S3/R2 y lo registra en la biblioteca.
  // Devuelve el item creado (con su URL pública). Reemplaza el viejo flujo base64.
  const uploadAndAddMedia = useCallback(async (file) => {
    const url = await blogApi.uploadImage(file);
    if (!url) throw new Error('No se recibió URL del archivo subido');
    const type = file.type.startsWith('video/') ? 'video' : 'image';
    return addMedia({ url, filename: file.name, type, size: file.size });
  }, [addMedia]);

  // --- Settings ---
  const updateSettings = useCallback(async (data) => {
    const merged = await blogApi.saveSettings(data);
    setSettings(merged);
    return merged;
  }, []);

  // --- Export / Import ---
  const exportPosts = useCallback(() => {
    const data = JSON.stringify(posts, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `physiology-blog-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [posts]);

  const importPosts = useCallback(async (jsonData) => {
    try {
      const imported = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      if (!Array.isArray(imported)) return { success: false, message: 'Invalid format' };
      let created = 0;
      for (const p of imported) {
        try {
          await blogApi.createPost(p);
          created += 1;
        } catch (e) {
          console.warn('BlogContext: fallo al importar un post', e);
        }
      }
      await refreshPosts();
      return { success: true, message: `Imported ${created} posts` };
    } catch {
      return { success: false, message: 'Failed to parse JSON' };
    }
  }, [refreshPosts]);

  // --- Computed ---
  const publishedPosts = useMemo(() => posts.filter(p => p.status === 'published'), [posts]);
  const draftPosts = useMemo(() => posts.filter(p => p.status === 'draft'), [posts]);

  const value = useMemo(() => ({
    posts, loading, getPost, getPostBySlug, createPost, updatePost, deletePost,
    exportPosts, importPosts,
    categories, addCategory, updateCategory, deleteCategory,
    media, addMedia, deleteMedia, uploadAndAddMedia,
    settings, updateSettings,
    publishedPosts, draftPosts,
    refreshPosts, refreshCategories, refreshMedia, refreshSettings,
  }), [posts, loading, getPost, getPostBySlug, createPost, updatePost, deletePost,
       exportPosts, importPosts,
       categories, addCategory, updateCategory, deleteCategory,
       media, addMedia, deleteMedia, uploadAndAddMedia,
       settings, updateSettings,
       publishedPosts, draftPosts,
       refreshPosts, refreshCategories, refreshMedia, refreshSettings]);

  return (
    <BlogContext.Provider value={value}>
      {children}
    </BlogContext.Provider>
  );
}

export function useBlog() {
  const context = useContext(BlogContext);
  if (!context) {
    throw new Error('useBlog must be used within a BlogProvider');
  }
  return context;
}
